import { calculateFinalPayableRounding } from "../commerce/final-payable-rounding";
import type { CustomerBenefitRuntimeItem } from "../gateway/customer-benefit-client";
import type { LoyaltyRewardCatalogRef, LoyaltyRewardSnapshot } from "../gateway/loyalty-redemption-client";
import type { PromotionRuntimeSnapshot } from "../gateway/promotion-runtime-client";
import {
  localCartSaleFingerprint,
  type LocalCartDraft,
} from "../local-store/cart-state";
import type { LocalLoyaltyRewardPricing } from "../local-store/loyalty-redemption-state";

function positiveNumber(value: unknown) {
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
}

function rewardSnapshot(value: Record<string, unknown>): LoyaltyRewardSnapshot | null {
  const item = value as Partial<LoyaltyRewardSnapshot>;
  if (
    typeof item.reward_definition_id !== "string"
    || typeof item.reward_version_id !== "string"
    || typeof item.version_no !== "number"
    || typeof item.name !== "string"
    || typeof item.point_cost !== "number"
    || typeof item.benefit_type !== "string"
    || !item.benefit_document
    || typeof item.benefit_document !== "object"
  ) return null;
  return {
    reward_definition_id: item.reward_definition_id,
    reward_version_id: item.reward_version_id,
    version_no: item.version_no,
    name: item.name,
    description: typeof item.description === "string" ? item.description : null,
    point_cost: item.point_cost,
    benefit_type: item.benefit_type,
    benefit_document: item.benefit_document,
    benefit_reference: item.benefit_reference ?? null,
    featured: item.featured === true,
    priority_rank: typeof item.priority_rank === "number" ? item.priority_rank : 100,
    catalog_refs: Array.isArray(item.catalog_refs) ? item.catalog_refs as LoyaltyRewardCatalogRef[] : [],
  };
}

export function rewardFromCustomerBenefit(item: CustomerBenefitRuntimeItem) {
  if (item.source_type !== "LOYALTY_REWARD" || !item.active) return null;
  return rewardSnapshot(item.benefit_snapshot);
}

function referenceMatches(ref: LoyaltyRewardCatalogRef, line: LocalCartDraft["lines"][number]) {
  if (ref.reference_type === "MENU_ITEM") return ref.reference_id === line.menuItemId;
  if (ref.reference_type === "SELLABLE_VARIANT") return ref.reference_id === line.menuVariantId;
  if (ref.reference_type === "MENU_CATEGORY") return ref.reference_id === line.menuCategoryId;
  return false;
}

function eligibleLineIds(reward: LoyaltyRewardSnapshot, cart: LocalCartDraft) {
  const refs = reward.catalog_refs ?? [];
  const includeRefs = refs.filter((ref) => (ref.match_mode ?? "INCLUDE") === "INCLUDE");
  const excludeRefs = refs.filter((ref) => (ref.match_mode ?? "INCLUDE") === "EXCLUDE");
  const scopeMode = String(reward.benefit_document.catalog_scope_mode ?? "ALL_ITEMS").toUpperCase();

  return cart.lines
    .filter((line) => {
      const included = scopeMode === "ALL_ITEMS" || !includeRefs.length || includeRefs.some((ref) => referenceMatches(ref, line));
      const excluded = excludeRefs.some((ref) => referenceMatches(ref, line));
      return included && !excluded;
    })
    .map((line) => line.lineId);
}

function policyValue(item: CustomerBenefitRuntimeItem, key: string) {
  const snapshot = item.policy_snapshot ?? {};
  const nested = snapshot.policy && typeof snapshot.policy === "object"
    ? snapshot.policy as Record<string, unknown>
    : {};
  return nested[key] ?? snapshot[key];
}

function assertCompatibility(
  benefit: CustomerBenefitRuntimeItem,
  cart: LocalCartDraft,
  promotionRuntimes: PromotionRuntimeSnapshot[],
) {
  const applied = cart.quote?.appliedPromotions ?? [];
  if (!applied.length) return;
  if (String(policyValue(benefit, "promotion_compatibility_mode") ?? "") !== "SHARED_COMPATIBILITY_RESOLVER") {
    throw new Error("LOYALTY_REWARD_COMPATIBILITY_POLICY_NOT_READY");
  }
  if (cart.quote?.quoteSource !== "LOCAL_PROMOTION_RUNTIME_V1") {
    throw new Error("LOYALTY_REWARD_COMPATIBILITY_SNAPSHOT_REQUIRED");
  }
  const byVersion = new Map(promotionRuntimes.map((runtime) => [runtime.promotion_version_id, runtime]));
  for (const promotion of applied) {
    const runtime = byVersion.get(promotion.promotionVersionId);
    if (!runtime) throw new Error("LOYALTY_REWARD_COMPATIBILITY_SNAPSHOT_REQUIRED");
    if (runtime.stacking_mode !== "STACKABLE") {
      throw new Error("LOYALTY_REWARD_PROMOTION_CONFLICT");
    }
  }
}

function eligiblePostPromotionAmount(reward: LoyaltyRewardSnapshot, cart: LocalCartDraft) {
  const quote = cart.quote;
  if (!quote || quote.cartFingerprint !== localCartSaleFingerprint(cart)) throw new Error("POS_SALE_QUOTE_REQUIRED");
  const ids = new Set(eligibleLineIds(reward, cart));
  if (!ids.size) return 0;

  const currencyScale = cart.lines[0]?.currencyScale;
  if (currencyScale == null || !Number.isInteger(currencyScale) || currencyScale < 0 || currencyScale > 6) {
    throw new Error("LOCAL_CURRENCY_SCALE_NOT_READY");
  }
  if (cart.lines.some((line) => line.currencyScale !== currencyScale || line.currencyCode !== quote.currencyCode)) {
    throw new Error("LOYALTY_REWARD_CURRENCY_MISMATCH");
  }

  const remaining = new Map(
    cart.lines.map((line) => [line.lineId, line.unitPrice * line.quantity]),
  );
  for (const promotion of quote.appliedPromotions) {
    if (!promotion.lineAllocations?.length) {
      if (ids.size === cart.lines.length) return Math.max(0, quote.postPromotionTotal);
      throw new Error("LOYALTY_REWARD_ELIGIBILITY_ALLOCATION_NOT_READY");
    }
    for (const allocation of promotion.lineAllocations) {
      remaining.set(allocation.lineId, Math.max(0, (remaining.get(allocation.lineId) ?? 0) - allocation.amount));
    }
  }
  return [...ids].reduce((sum, id) => sum + Math.max(0, remaining.get(id) ?? 0), 0);
}

export function resolveLocalReservedReward(input: {
  benefit: CustomerBenefitRuntimeItem;
  cart: LocalCartDraft;
  promotionRuntimes: PromotionRuntimeSnapshot[];
}): { reward: LoyaltyRewardSnapshot; pricing: LocalLoyaltyRewardPricing } {
  const { benefit, cart } = input;
  if (benefit.source_type !== "LOYALTY_REWARD" || !benefit.active || !["RESERVED", "APPLIED"].includes(benefit.state)) {
    throw new Error("LOYALTY_REWARD_RESERVATION_NOT_READY");
  }
  if (!cart.selectedMember || cart.selectedMember.customerId !== benefit.customer_id) {
    throw new Error("LOYALTY_REWARD_CART_CUSTOMER_MISMATCH");
  }
  if (benefit.valid_until && Date.parse(benefit.valid_until) <= Date.now()) {
    throw new Error("LOYALTY_REWARD_RESERVATION_EXPIRED");
  }
  const reward = rewardFromCustomerBenefit(benefit);
  if (!reward) throw new Error("LOYALTY_REWARD_SNAPSHOT_INVALID");
  if (reward.benefit_type !== "FIXED_AMOUNT_DISCOUNT") {
    throw new Error("LOYALTY_REWARD_BENEFIT_NOT_SUPPORTED_LOCALLY");
  }
  if (!cart.commerceCalculation) throw new Error("FINAL_PAYABLE_SETTINGS_NOT_READY");

  assertCompatibility(benefit, cart, input.promotionRuntimes);
  const eligibleAmount = eligiblePostPromotionAmount(reward, cart);
  if (eligibleAmount <= 0) throw new Error("LOYALTY_REWARD_NO_ELIGIBLE_ORDER_AMOUNT");

  const requestedDiscount = positiveNumber(reward.benefit_document.amount);
  if (requestedDiscount <= 0) throw new Error("LOYALTY_REWARD_BENEFIT_AMOUNT_INVALID");
  const discount = Math.min(requestedDiscount, eligibleAmount, cart.quote?.postPromotionTotal ?? eligibleAmount);
  if (discount <= 0) throw new Error("LOYALTY_REWARD_NO_ELIGIBLE_ORDER_AMOUNT");

  const preRounding = Math.max(0, Number(((cart.quote?.postPromotionTotal ?? 0) - discount).toFixed(2)));
  const rounded = calculateFinalPayableRounding(preRounding, cart.commerceCalculation);
  return {
    reward,
    pricing: {
      cartFingerprint: localCartSaleFingerprint(cart),
      appliedDiscountAmount: Number(discount.toFixed(2)),
      totalAmount: rounded.totalAmount,
      roundingAmount: rounded.roundingAmount,
      currencyCode: cart.quote?.currencyCode ?? cart.lines[0]?.currencyCode ?? "THB",
      quotedAt: new Date().toISOString(),
    },
  };
}
